import { FullTextReviewVote } from '@/models';
import {
  CreateFullTextReviewVote,
  ChangeFullTextReviewVote,
  RemoveFullTextReviewVote,
} from '@/graphql';
import { AssessmentState, ScreeningOutcomeResolution } from '@/enums';
import { apolloClient } from '@/plugins/apollo';
import { v4 as createUUID } from 'uuid';
import {
  appendToListSubQueryCache,
  deleteFromListSubQueryCacheWhere,
} from '@/modules/cacheHelpers';

const createOptimisticResponseForSaveVote = async (
  context,
  fullTextReviewVote,
  isUpdate
) => {
  const optimisticId = isUpdate
    ? fullTextReviewVote.id
    : createUUID();
  const fullTextReviewOutcomeOptimisticId = createUUID();

  const existingRef = fullTextReviewVote.reference;
  let fullTextReviewState = existingRef.fullTextReviewState;
  let fullTextReviewOutcome = existingRef.fullTextReviewOutcome?.id
    ? existingRef.fullTextReviewOutcome
    : null;
  if (!isUpdate) {
    const requiredVotes = await context.dispatch(
      'review/fetchRequiredFullTextReviewVotes',
      existingRef.review.id,
      { root: true }
    );
    fullTextReviewState =
      requiredVotes > 1
        ? AssessmentState.PARTIAL
        : AssessmentState.COMPLETE;
    fullTextReviewOutcome =
      requiredVotes > 1
        ? fullTextReviewOutcome
        : {
            id: fullTextReviewOutcomeOptimisticId,
            optimisticId: fullTextReviewOutcomeOptimisticId,
            outcome: fullTextReviewVote.vote,
            resolution: ScreeningOutcomeResolution.UNANIMOUS_VOTE,
            resolvedBy: null,
            reference: {
              id: existingRef.id,
              __typename: 'Reference',
            },
            __typename: 'FullTextReviewOutcome',
          };
  }
  const key = isUpdate
    ? 'changeFullTextReviewVote'
    : 'createFullTextReviewVote';
  return {
    [key]: {
      ...fullTextReviewVote,
      id: optimisticId,
      optimisticId,
      reference: {
        ...fullTextReviewVote.reference,
        fullTextReviewState,
        fullTextReviewOutcome,
      },
      __typename: 'FullTextReviewVote',
    },
  };
};

const actions = {
  saveAndAwaitRefetch(context, fullTextReviewVote) {
    return context.dispatch('internalSave', {
      fullTextReviewVote,
      awaitRefetch: true,
    });
  },
  saveOptimistically(context, fullTextReviewVote) {
    return context.dispatch('internalSave', {
      fullTextReviewVote,
      optimistic: true,
    });
  },
  save(context, fullTextReviewVote) {
    return context.dispatch('internalSave', {
      fullTextReviewVote,
    });
  },
  async internalSave(
    context,
    { fullTextReviewVote, awaitRefetch = false, optimistic = false }
  ) {
    const isUpdate =
      fullTextReviewVote.id && !fullTextReviewVote.optimisticId;
    const mutation = isUpdate
      ? ChangeFullTextReviewVote
      : CreateFullTextReviewVote;

    const refetchQueries = [
      'GetReference',
      'GetReferencesByReviewIdAndFilters',
      'GetReferencesByFilters',
      'SearchReferences',
      'GetRelevanceAssessmentCountsByReviewIdAndFilters',
      'GetTopReferencesForScreening',
      'GetFullTextReviewPage',
    ];

    let optimisticResponse = undefined;
    if (optimistic) {
      optimisticResponse = await createOptimisticResponseForSaveVote(
        context,
        fullTextReviewVote,
        isUpdate
      );
    }

    const response = await apolloClient.mutate({
      mutation,
      variables: {
        fullTextReviewVote: fullTextReviewVote.creationData(),
      },
      optimisticResponse,
      update(cache, { data }) {
        const referenceId = fullTextReviewVote.reference.id;
        deleteFromListSubQueryCacheWhere(
          cache,
          referenceId,
          'Reference',
          'fullTextReviewVotes',
          ({ item, readField }) =>
            readField('id', readField('reviewer', item)) ===
            fullTextReviewVote.reviewer.id
        );
        appendToListSubQueryCache(
          cache,
          referenceId,
          'Reference',
          'fullTextReviewVotes',
          data?.createFullTextReviewVote ||
            data?.changeFullTextReviewVote,
          0
        );
      },
      refetchQueries: optimistic ? [] : refetchQueries,
      awaitRefetchQueries: awaitRefetch,
    });
    const data = fullTextReviewVote.id
      ? response.data.changeFullTextReviewVote
      : response.data.createFullTextReviewVote;
    return new FullTextReviewVote(data);
  },
  async delete(context, fullTextReviewVote) {
    const response = await apolloClient.mutate({
      mutation: RemoveFullTextReviewVote,
      variables: {
        id: fullTextReviewVote?.id || fullTextReviewVote,
      },
      refetchQueries: [
        'GetReferencesByReviewIdAndFilters',
        'GetReferencesByFilters',
      ],
      awaitRefetchQueries: true,
    });
    return new FullTextReviewVote(
      response.data.removeFullTextReviewVote
    );
  },
  async deleteWithCacheUpdate(context, { id, cacheUpdate }) {
    const response = await apolloClient.mutate({
      mutation: RemoveFullTextReviewVote,
      variables: {
        id,
      },
      update(cache, { data }) {
        cacheUpdate(cache, data.removeFullTextReviewVote);
      },
    });
    return new FullTextReviewVote(
      response.data.removeFullTextReviewVote
    );
  },
};

export default {
  namespaced: true,
  actions,
};
